import { useEffect, useState } from "react";
import { useToolbar } from "../../components/ToolbarContext";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  InputLabel,
  Divider
} from "@mui/material";
import { Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { CompanyCrewMemberDto, CrewCandidateDto } from "./dtos/company-crew-member.dto";
import { JobCrewMemberDto, AddOrUpdateJobCrewMemberDto, UpdateJobCrewMembersDto } from "./dtos/job-crew-member.dto";
import { getAllCrewMembers, getCrewCandidates, getJobCrewMembers, updateJobCrewMembers, deleteCrewMember } from "./services/crew-member.service";
import { enqueueSnackbar } from "notistack";
import { CrewMemberDialog } from "./components/CrewMemberDialog";
import { createNewEstimateTime, getEstimateTime, updateEstimateTime } from "../estimate/estimate-time.service";
import { EstimateTimeDto } from "../../shared/dtos/estimate-time.dto";
import { getAllOperations } from "../equipment/services/operation-method.service";
import { getTreeOperationMethods, updateTreeOperationMethods } from "../estimate/estimate-tree.service";
import { CustomOperationMethodDto } from "../equipment/dtos/operation-method.dto";
import { EstimateTreeOperationMethodDto, UpdateEstimateTreeOperationMethodDto } from "../../shared/dtos/estimate-tree-operation-method.dto";
import { EstimateTreeDto } from "../../shared/dtos/estimate-tree.dto";
import { completeWorkOrder, getWorkOrder, updateWorkOrder } from "../work-order/work-order.service";
import { completeWorkOrderAndMarkAsPaid } from "../work-order/work-order.service";
import { WorkOrderDto } from "../work-order/dtos/work-order.dto";
import {usePermissions} from "../../shared/permissions/PermissionContext";
import {PermissionCode} from "../../shared/permissions/permissions";
import {minutesToSeconds, secondsToMinutes} from "../../shared/services/operation-time.service";

interface HoursAndMinutes {
  hours: number;
  minutes: number;
}

interface TreeWithOperationMethods {
  tree: EstimateTreeDto;
  treeLocalId: number;
  availableOperationMethods: CustomOperationMethodDto[];
  selectedOperationMethodIds: number[];
  savedOperationMethods: EstimateTreeOperationMethodDto[];
}

const ToolbarActions = ({ onSubmit, onBack }: { onSubmit: () => void, onBack: () => void }) => {
  return (
    <div style={{ display: 'flex', gap: '10px' }}>
      <Button variant='outlined' onClick={onBack}>Back to Job</Button>
    </div>
  );
};

export const CompleteJobPage = () => {
  const navigate = useNavigate();
  const {hasPermission} = usePermissions();
  const canComplete = hasPermission(PermissionCode.JobsComplete);
  const canCreateCrewMember = hasPermission(PermissionCode.TeamCreate);
  const canEditCrewMember = hasPermission(PermissionCode.TeamEdit);
  const canDeleteCrewMember = hasPermission(PermissionCode.TeamDelete);
  const canViewRates = hasPermission(PermissionCode.TeamViewList);
  const routeParams = useParams<Record<string, string>>();
  const workOrderId = parseInt(routeParams["workOrderId"]!);
  const toolbarContext = useToolbar();
  const [workOrder, setWorkOrder] = useState<WorkOrderDto | null>(null);
  const [allCrewMembers, setAllCrewMembers] = useState<CompanyCrewMemberDto[]>([]);
  // SQ-522: candidates that cannot be picked (no billable rate on their profile).
  const [unselectableCrewIds, setUnselectableCrewIds] = useState<Set<number>>(new Set());
  const [savedCrewMembers, setSavedCrewMembers] = useState<JobCrewMemberDto[]>([]);
  const [savedEstimateTime, setSavedEstimateTime] = useState<EstimateTimeDto | null>(null);
  const [selectedCrewMembers, setSelectedCrewMembers] = useState<AddOrUpdateJobCrewMemberDto[]>([]);
  const [totalJobDuration, setTotalJobDuration] = useState<HoursAndMinutes>({ hours: 0, minutes: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [isAddCrewMemberDialogOpen, setIsAddCrewMemberDialogOpen] = useState(false);
  const [, setIsSubmitting] = useState(false);
  const [crewMemberToEdit, setCrewMemberToEdit] = useState<CompanyCrewMemberDto | undefined>();
  const [treesWithOperationMethods, setTreesWithOperationMethods] = useState<TreeWithOperationMethods[]>([]);
  const [workOrderNotes, setWorkOrderNotes] = useState<string>('');
  const [addPaymentValue, setAddPaymentValue] = useState<string>('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [crewMemberToDelete, setCrewMemberToDelete] = useState<CompanyCrewMemberDto | null>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const fetchedWorkOrder = await getWorkOrder(navigate, workOrderId);

        if (!fetchedWorkOrder) {
          throw new Error('Job not found');
        }
        setWorkOrder(fetchedWorkOrder);

        const [candidates, jobCrewMembers, estimateTime, allOperationMethods] = await Promise.all([
          getCrewCandidates(navigate),
          getJobCrewMembers(navigate, fetchedWorkOrder.id),
          getEstimateTime(navigate, fetchedWorkOrder.estimateId),
          getAllOperations(navigate)
        ]);

        // SQ-522: the crew is picked from TEAM MEMBERS (the candidates, each
        // standing for a crew row), plus any legacy crew-list member already
        // on this job, who is shown from the job's own row.
        const asCrewMember = (c: CrewCandidateDto): CompanyCrewMemberDto => ({
          id: c.companyCrewMemberId, companyId: 0, name: c.name, role: c.role || 'Groundsman',
          hourlyRate: c.billableHourlyRate, createdBy: 0, createdAt: '', updatedAt: ''
        });
        const candidateIds = new Set(candidates.map(c => c.companyCrewMemberId));
        const legacyOnJob: CompanyCrewMemberDto[] = jobCrewMembers
          .filter(cm => !candidateIds.has(cm.companyCrewMemberId))
          .map(cm => ({
            id: cm.companyCrewMemberId, companyId: 0, name: cm.name, role: cm.role,
            hourlyRate: cm.hourlyRate, createdBy: 0, createdAt: '', updatedAt: ''
          }));
        setAllCrewMembers([...candidates.map(asCrewMember), ...legacyOnJob]);
        setUnselectableCrewIds(new Set(candidates.filter(c => !c.selectable).map(c => c.companyCrewMemberId)));
        setSavedCrewMembers(jobCrewMembers);
        setSelectedCrewMembers(jobCrewMembers.map(cm => ({
          companyCrewMemberId: cm.companyCrewMemberId,
          role: cm.role,
          hourlyRate: cm.hourlyRate,
          secondsWorked: cm.secondsWorked
        })));
        setTotalJobDuration(estimateTime ? {
          hours: Math.floor(estimateTime.elapsedSeconds / 3600),
          minutes: Math.floor((estimateTime.elapsedSeconds % 3600) / 60)
        } : { hours: 0, minutes: 0 });
        setSavedEstimateTime(estimateTime);
        
        setWorkOrderNotes(fetchedWorkOrder.notes || '');
        const treeIdToLocalIdMap: {[key: number]: number} = {};
        fetchedWorkOrder.estimateData.trees
          .map(t => t.id)
          .sort((a, b) => a - b)
          .forEach((id, i) => {
            treeIdToLocalIdMap[id] = i + 1; // Start the ids at 1 for the user
          });

        const treesWithMethods = await Promise.all(
          fetchedWorkOrder.estimateData.trees.map(async (tree) => {
            const savedOperationMethods = await getTreeOperationMethods(navigate, fetchedWorkOrder.estimateId, tree.id);
            return {
              tree,
              treeLocalId: treeIdToLocalIdMap[tree.id],
              availableOperationMethods: allOperationMethods,
              selectedOperationMethodIds: savedOperationMethods.map(method => method.customOperationMethodId),
              savedOperationMethods
            };
          })
        );
        setTreesWithOperationMethods(treesWithMethods);
      } catch (error) {
        console.error('Failed to load data:', error);
        enqueueSnackbar('Failed to load the job', { variant: 'error' });
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [workOrderId, navigate]);

  const getCrewMemberTime = (crewMemberId: number) => {
    const crewMember = selectedCrewMembers.find(scm => scm.companyCrewMemberId === crewMemberId);
    if (!crewMember) return { hours: 0, minutes: 0 };
    const totalMinutes = secondsToMinutes(crewMember.secondsWorked);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return { hours, minutes };
  };

  useEffect(() => {
    toolbarContext.initToolbar('CompleteJobPage', 'Complete Job', {}, () => <ToolbarActions onSubmit={handleSubmit} onBack={handleBackToWorkOrderClick} />);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCrewMembers, savedCrewMembers, totalJobDuration, treesWithOperationMethods, workOrderNotes, addPaymentValue]);

  const handleBackToWorkOrderClick = () => {
    navigate(`/work-orders/${workOrderId}/itemized`);
  };

  const handleCrewMemberSelectionToggle = (crewMemberId: number) => {
    setSelectedCrewMembers(prev => {
      if (prev.find(scm => scm.companyCrewMemberId === crewMemberId)) {
        return prev.filter(scm => scm.companyCrewMemberId !== crewMemberId);
      } else {
        const crewMember = allCrewMembers.find(cm => cm.id === crewMemberId);
        return [...prev, {
          companyCrewMemberId: crewMemberId,
          role: crewMember?.role || '',
          hourlyRate: canViewRates ? (crewMember?.hourlyRate ?? 0) : null,
          secondsWorked: 0
        }];
      }
    });
  };

  const handleTimeChange = (crewMemberId: number, field: keyof HoursAndMinutes, value: number) => {
    const currentTime = getCrewMemberTime(crewMemberId);
    const newTime = { ...currentTime };
    if (field === 'hours') {
      newTime.hours = value;
    } else {
      newTime.minutes = value;
    }
    const secondsWorked = minutesToSeconds((newTime.hours * 60) + newTime.minutes);
    setSelectedCrewMembers(prev => prev.map(scm => scm.companyCrewMemberId === crewMemberId ? { ...scm, secondsWorked } : scm));
  };

  const handleApplySameTime = () => {
    const firstSelectedTime = selectedCrewMembers.find(scm => scm.secondsWorked > 0)?.secondsWorked;
    if (!firstSelectedTime) return;

    setSelectedCrewMembers(prev => prev.map(scm => ({ ...scm, secondsWorked: firstSelectedTime })));
  };

  const calculateTotalTime = (role: string) => {
    // Added up in seconds and rounded once (SQ-520).
    const totalMinutes = secondsToMinutes(selectedCrewMembers.filter(scm => scm.role === role).reduce((acc, scm) => {
      return acc + scm.secondsWorked;
    }, 0));


    return {
      hours: Math.floor(totalMinutes / 60),
      minutes: totalMinutes % 60
    };
  };

  const handleAddCrewMember = () => {
    setCrewMemberToEdit(undefined);
    setIsAddCrewMemberDialogOpen(true);
  };

  const handleEditCrewMember = (crewMember: CompanyCrewMemberDto) => {
    setCrewMemberToEdit(crewMember);
    setIsAddCrewMemberDialogOpen(true);
  };

  const handleCrewMemberAdded = async () => {
    try {
      // A member added or edited through the legacy crew-list dialog: merge
      // the list into what the page shows (the team candidates stay).
      const legacy = await getAllCrewMembers(navigate);
      setAllCrewMembers(prev => [
        ...prev.map(p => legacy.find(l => l.id === p.id) ?? p),
        ...legacy.filter(l => !prev.some(p => p.id === l.id)),
      ]);
    } catch (error) {
      console.error('Failed to refresh crew members:', error);
    }
  };

  const handleDeleteCrewMember = (crewMember: CompanyCrewMemberDto) => {
    setCrewMemberToDelete(crewMember);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!crewMemberToDelete) return;

    try {
      await deleteCrewMember(navigate, crewMemberToDelete.id);
      
      // Remove from selected crew members if it was selected
      setSelectedCrewMembers(prev => 
        prev.filter(scm => scm.companyCrewMemberId !== crewMemberToDelete.id)
      );
      
      // Refresh the list: a deleted legacy crew-list member is gone; the
      // team candidates stay (SQ-522).
      setAllCrewMembers(prev => prev.filter(cm => cm.id !== crewMemberToDelete.id));
      
      enqueueSnackbar('Crew member deleted successfully', { variant: 'success' });
    } catch (error) {
      console.error('Failed to delete crew member:', error);
      enqueueSnackbar('Failed to delete crew member', { variant: 'error' });
    } finally {
      setDeleteConfirmOpen(false);
      setCrewMemberToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setCrewMemberToDelete(null);
  };

  const handleCrewMemberRoleChange = (crewMemberId: number, role: string) => {
    setSelectedCrewMembers(prev => prev.map(scm => scm.companyCrewMemberId === crewMemberId ? { ...scm, role } : scm));
  };

  const handleCrewMemberRateChange = (crewMemberId: number, rate: number) => {
    setSelectedCrewMembers(prev => prev.map(scm => scm.companyCrewMemberId === crewMemberId ? { ...scm, hourlyRate: rate } : scm));
  };

  const getCrewMemberRate = (crewMemberId: number) => {
    const crewMember = allCrewMembers.find(cm => cm.id === crewMemberId);
    if (!crewMember) {
      console.error('Crew member not found', crewMemberId);
      return 0;
    };
    const selectedData = selectedCrewMembers.find(scm => scm.companyCrewMemberId === crewMemberId);
    if (!selectedData || selectedData.hourlyRate === undefined || selectedData.hourlyRate === null) {
      return crewMember.hourlyRate;
    }
    return (selectedData.hourlyRate).toString(); // We have to toString this because otherwise the input will not trim leading zeros
  };
  
  const handleOperationMethodsChange = (treeId: number, selectedMethodIds: number[]) => {
    setTreesWithOperationMethods(prev => 
      prev.map(treeWithMethods => 
        treeWithMethods.tree.id === treeId 
          ? { ...treeWithMethods, selectedOperationMethodIds: selectedMethodIds }
          : treeWithMethods
      )
    );
  };

  const handleWorkOrderNotesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setWorkOrderNotes(event.target.value);
  };

  const handleAddPaymentChange = (event: any) => {
    setAddPaymentValue(event.target.value);
  };

  const validateSubmission = (): boolean => {
    if(!workOrder?.id) {
      enqueueSnackbar('Job not found', { variant: 'error' });
      return false;
    }

    if (selectedCrewMembers.length === 0) {
      enqueueSnackbar('Please select at least one crew member', { variant: 'error' });
      return false;
    }

    if (totalJobDuration.hours === 0 && totalJobDuration.minutes === 0) {
      enqueueSnackbar('Please enter the total job duration', { variant: 'error' });
      return false;
    }

    return true;
  };

  const updateCrewMembers = async (): Promise<void> => {
    const crewMembersToAdd = selectedCrewMembers.filter(scm => 
      !savedCrewMembers.find(sc => sc.companyCrewMemberId === scm.companyCrewMemberId)
    );
    
    const crewMembersToUpdate = selectedCrewMembers.filter(scm => 
      savedCrewMembers.find(sc => 
        sc.companyCrewMemberId === scm.companyCrewMemberId && 
        (sc.role !== scm.role
          || (canViewRates && sc.hourlyRate !== scm.hourlyRate)
          || sc.secondsWorked !== scm.secondsWorked)
      )
    );
    
    const crewMemberIdsToRemove = savedCrewMembers
      .filter(scm => !selectedCrewMembers.find(sc => sc.companyCrewMemberId === scm.companyCrewMemberId))
      .map(scm => scm.id);

    const forPatch = (cm: AddOrUpdateJobCrewMemberDto): AddOrUpdateJobCrewMemberDto =>
      canViewRates ? cm : { companyCrewMemberId: cm.companyCrewMemberId, role: cm.role, secondsWorked: cm.secondsWorked };

    const updateDto: UpdateJobCrewMembersDto = {
      crewMembersToAdd: crewMembersToAdd.map(forPatch),
      crewMembersToUpdate: crewMembersToUpdate.map(forPatch),
      crewMemberIdsToRemove
    };

    await updateJobCrewMembers(navigate, workOrder!.id, updateDto);
  };

  const updateOperationMethods = async (): Promise<void> => {
    await Promise.all(
      treesWithOperationMethods.map(async (treeWithMethods) => {
        const operationMethodIdsToAdd = treeWithMethods.selectedOperationMethodIds.filter(
          methodId => !treeWithMethods.savedOperationMethods.find(savedMethod => savedMethod.customOperationMethodId === methodId)
        );
        
        const operationMethodIdsToRemove = treeWithMethods.savedOperationMethods
          .filter(method => !treeWithMethods.selectedOperationMethodIds.includes(method.customOperationMethodId))
          .map(method => method.customOperationMethodId);

        if (operationMethodIdsToAdd.length > 0 || operationMethodIdsToRemove.length > 0) {
          const update: UpdateEstimateTreeOperationMethodDto = {
            operationIdsToAdd: operationMethodIdsToAdd,
            operationIdsToRemove: operationMethodIdsToRemove
          };
          await updateTreeOperationMethods(navigate, workOrder!.estimateId, treeWithMethods.tree.id, update);
        }
      })
    );
  };

  const updateEstimateTimeData = async (): Promise<void> => {
    const totalJobDurationMinutes = (totalJobDuration.hours * 60) + totalJobDuration.minutes;
    
    if (savedEstimateTime) {
      await updateEstimateTime(navigate, workOrder!.estimateId, savedEstimateTime.id, {
        isStart: false, 
        elapsedSeconds: totalJobDurationMinutes * 60
      });
    } else {
      await createNewEstimateTime(navigate, workOrder!.estimateId, {
        type: 'job_length', 
        elapsedSeconds: totalJobDurationMinutes * 60, 
        startRunning: false
      });
    }
  };

  const saveNotesIfChanged = async () => {
    if(workOrderNotes !== workOrder?.notes) {
      await updateWorkOrder(navigate, workOrder!.id, {
        notes: workOrderNotes
      });
    }
  }

  const handleSubmit = async () => {
    if (!validateSubmission()) {
      return;
    }

    try {
      setIsSubmitting(true);

      await Promise.all([
        updateOperationMethods(),
        updateEstimateTimeData(),
        updateCrewMembers(),
        saveNotesIfChanged()
      ]);
      
      // TODO: Update job notes (need to implement estimate update service)
      // TODO: Handle customer paid functionality (need to implement payment service)
      if(addPaymentValue === 'Customer Paid') {
        await completeWorkOrderAndMarkAsPaid(navigate, workOrder!.id); // Job was validated in validateSubmission
      } else {
        await completeWorkOrder(navigate, workOrder!.id); // Job was validated in validateSubmission
      }

      enqueueSnackbar('Job completed successfully', { variant: 'success' });
      navigate(`/work-orders/${workOrder!.id}/itemized`);
    } catch (error) {
      console.error('Failed to complete job:', error);
      // SQ-559: the server's "Crew time missing: …" refusal names who has no time.
      const message = error instanceof Error && error.message.startsWith('Crew time missing') ? error.message : 'Failed to complete job';
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading || !workOrder) {
    return <div>Loading...</div>;
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Customer Information Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Customer Information</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography><strong>Customer Name:</strong> {workOrder.clientName}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography><strong>Job Number:</strong> {workOrder.id}</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Crew Info and Time Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Crew Information</Typography>

          {canCreateCrewMember && (
            <Button
              variant="contained"
              color="primary"
              sx={{ mb: 2 }}
              onClick={handleAddCrewMember}
            >
              Add Crew Member
            </Button>
          )}

          {/* Crew Member List */}
          <Stack spacing={2}>
            {allCrewMembers.filter(member => member.isActive !== false || selectedCrewMembers.some(selected => selected.companyCrewMemberId === member.id)).map((member) => (
              <Paper
                key={member.id}
                sx={{
                  p: 2,
                  backgroundColor: selectedCrewMembers.find(scm => scm.companyCrewMemberId === member.id) ? 'action.selected' : 'background.paper'
                }}
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Checkbox
                        checked={selectedCrewMembers.find(scm => scm.companyCrewMemberId === member.id) !== undefined}
                        disabled={unselectableCrewIds.has(member.id) && selectedCrewMembers.find(scm => scm.companyCrewMemberId === member.id) === undefined}
                        onChange={() => handleCrewMemberSelectionToggle(member.id)}
                      />
                      <Typography>{member.name}</Typography>
                      {unselectableCrewIds.has(member.id) && (
                        <Typography variant="caption" color="error">
                          Set a billable rate on their team profile before using them on a job.
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Role</InputLabel>
                      <Select
                        disabled={selectedCrewMembers.find(scm => scm.companyCrewMemberId === member.id) === undefined}
                        value={selectedCrewMembers.find(scm => scm.companyCrewMemberId === member.id)?.role || member.role}
                        onChange={(e) => handleCrewMemberRoleChange(member.id, e.target.value)}
                        label="Role"
                      >
                        <MenuItem value="Climber">Climber</MenuItem>
                        <MenuItem value="Groundsman">Groundsman</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  {canViewRates && (
                  <Grid item xs={12} sm={2}>
                    <FormControl fullWidth>
                      <TextField
                        disabled={selectedCrewMembers.find(scm => scm.companyCrewMemberId === member.id) === undefined}
                        value={getCrewMemberRate(member.id)}
                        onChange={(e) => handleCrewMemberRateChange(member.id, Number(e.target.value))}
                        size="small"
                        type="number"
                        label="Rate"
                        sx={{ width: '100%' }}
                        InputProps={{
                          startAdornment: '$',
                          inputProps: { min: 0 }
                        }}
                      />
                    </FormControl>
                  </Grid>
                  )}
                  <Grid item xs={12} sm={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Hours</InputLabel>
                      <Select
                        value={getCrewMemberTime(member.id).hours}
                        onChange={(e) => handleTimeChange(member.id, 'hours', Number(e.target.value))}
                        disabled={selectedCrewMembers.find(scm => scm.companyCrewMemberId === member.id) === undefined}
                        label="Hours"
                      >
                        {[...Array(100)].map((_, i) => (
                          <MenuItem key={i} value={i}>{i}h</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Minutes</InputLabel>
                      <Select
                        value={getCrewMemberTime(member.id).minutes}
                        onChange={(e) => handleTimeChange(member.id, 'minutes', Number(e.target.value))}
                        disabled={selectedCrewMembers.find(scm => scm.companyCrewMemberId === member.id) === undefined}
                        label="Minutes"
                      >
                        {[...Array(6)].map((_, i) => (
                          <MenuItem key={i} value={i * 10}>{i * 10}m</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={1} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    {canEditCrewMember && (
                      <IconButton
                        size="small"
                        onClick={() => handleEditCrewMember(member)}
                        disabled={!!selectedCrewMembers.find(scm => scm.companyCrewMemberId === member.id)} // If the crew member is already selected, don't allow editing as it creates a confusing experience with the overrides
                      >
                        <EditIcon />
                      </IconButton>
                    )}
                    {canDeleteCrewMember && (
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteCrewMember(member)}
                        disabled={!!selectedCrewMembers.find(scm => scm.companyCrewMemberId === member.id)} // Don't allow deleting if currently selected for this job
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Stack>

          {/* Apply Same Time Button */}
          {selectedCrewMembers.length > 0 && (
            <Button
              variant="outlined"
              onClick={handleApplySameTime}
              sx={{ mt: 2 }}
            >
              Apply Same Time for All
            </Button>
          )}

          {/* Crew Summary Totals */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" gutterBottom>Crew Summary</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography>
                  <strong>Total Climber Time:</strong> {calculateTotalTime('Climber').hours}h {calculateTotalTime('Climber').minutes}m
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography>
                  <strong>Total Grounds Crew Time:</strong> {calculateTotalTime('Groundsman').hours}h {calculateTotalTime('Groundsman').minutes}m
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography>
                  <strong>Total Crew Time:</strong> {(calculateTotalTime('Climber').hours + calculateTotalTime('Groundsman').hours)}h {(calculateTotalTime('Climber').minutes + calculateTotalTime('Groundsman').minutes)}m
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>

      {/* Total Job Duration Section */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Total Job Duration</Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl sx={{ width: 120 }}>
              <Select
                value={totalJobDuration.hours}
                onChange={(e) => setTotalJobDuration(prev => ({ ...prev, hours: Number(e.target.value) }))}
              >
                {[...Array(100)].map((_, i) => (
                  <MenuItem key={i} value={i}>{i} hours</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ width: 120 }}>
              <Select
                value={totalJobDuration.minutes}
                onChange={(e) => setTotalJobDuration(prev => ({ ...prev, minutes: Number(e.target.value) }))}
              >
                {[...Array(6)].map((_, i) => (
                  <MenuItem key={i} value={i * 10}>{i * 10} minutes</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {/* Operation Methods Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Operation Methods</Typography>
          {treesWithOperationMethods.map((treeWithMethods, index) => (
            <Box key={treeWithMethods.tree.id} sx={{ mb: index < treesWithOperationMethods.length - 1 ? 3 : 0 }}>
              {/* Tree Information */}
              <Paper sx={{ p: 2, mb: 2, backgroundColor: 'action.hover' }}>
                <Typography variant="h6" gutterBottom>
                  Tree #{treeWithMethods.treeLocalId}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="body2">
                      <strong>Species:</strong> {treeWithMethods.tree.species || 'Unknown'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="body2">
                      <strong>Height:</strong> {treeWithMethods.tree.height || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="body2">
                      <strong>Width:</strong> {treeWithMethods.tree.width || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="body2">
                      <strong>Diameter:</strong> {treeWithMethods.tree.trunkDiameter || 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Operation Methods List */}
              <Box sx={{ pl: 2 }}>
                <Typography variant="subtitle1" gutterBottom>Operation Methods:</Typography>
                <Grid container spacing={1}>
                  {treeWithMethods.availableOperationMethods.map((method) => (
                    <Grid item xs={12} sm={6} md={4} key={method.id}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={treeWithMethods.selectedOperationMethodIds.includes(method.id)}
                            onChange={(e) => {
                              const newSelectedIds = e.target.checked
                                ? [...treeWithMethods.selectedOperationMethodIds, method.id]
                                : treeWithMethods.selectedOperationMethodIds.filter(id => id !== method.id);
                              handleOperationMethodsChange(treeWithMethods.tree.id, newSelectedIds);
                            }}
                          />
                        }
                        label={method.name}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>

              {/* Visual break between trees */}
              {index < treesWithOperationMethods.length - 1 && (
                <Divider sx={{ mt: 2 }} />
              )}
            </Box>
          ))}
        </CardContent>
      </Card>

      {/* Job Notes Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Job Notes</Typography>
          <TextField
            fullWidth
            multiline
            inputProps={{autoCapitalize: 'sentences'}}
            rows={4}
            value={workOrderNotes}
            onChange={handleWorkOrderNotesChange}
            placeholder="Enter job notes..."
            variant="outlined"
          />
        </CardContent>
      </Card>

      {/* Customer Paid Field */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Add Payment?</Typography>
          <FormControl fullWidth>
            <Select
              value={addPaymentValue}
              onChange={handleAddPaymentChange}
              displayEmpty
              placeholder="Add Payment?"
            >
              <MenuItem value="">
                <em>Add Payment?</em>
              </MenuItem>
              <MenuItem value="Customer Paid">Customer Paid</MenuItem>
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            {canComplete && (
            <Button
              variant='contained'
              color='primary'
              onClick={handleSubmit}
          >
              Complete Job
            </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      <CrewMemberDialog
        open={isAddCrewMemberDialogOpen}
        onClose={() => setIsAddCrewMemberDialogOpen(false)}
        onCrewMemberAdded={handleCrewMemberAdded}
        crewMemberToEdit={crewMemberToEdit}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCancelDelete}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Delete Crew Member
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete "{crewMemberToDelete?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompleteJobPage;
